import type { ReactElement, ReactNode } from 'react';
import { Link } from 'react-router';
import { initialsOf } from './initials';
import { Icon } from './Icon';
import { guessName } from '../lib/note';

/** A person is a plate, never a bare word. Round for a human. */
export function Who({
  name,
  email,
  me = false,
  to,
  size,
  short = false,
}: {
  name: string | null | undefined;
  email: string;
  me?: boolean;
  to?: string;
  size?: 'lg';
  /** First name on the plate, initials from the whole name — for a header, not a list. */
  short?: boolean;
}): ReactElement {
  const unnamed = !name && !email;
  // ⚠ Never the raw address as a label. A guest from another company has no
  // account here and may never have spoken, so `name` is null for exactly the
  // people a person is least likely to recognise from an address — and
  // "a.novichikhin@traceair.net" is not who anybody calls them (Vladimir,
  // 2026-09-20). The address is still one hover away, and the whole of it is
  // on their page.
  const known = name ?? (email ? guessName(email) : null);
  const label = short && known ? known.split(' ')[0] : (known ?? email);
  const body = (
    <>
      <span className={`avatar ${size ?? ''}`}>{unnamed ? '?' : initialsOf(known, email)}</span>
      <span className="truncate">{unnamed ? 'who is this?' : label}</span>
    </>
  );
  const classes = ['who', me ? 'me' : '', unnamed ? 'unnamed' : ''].filter(Boolean).join(' ');
  return to ? (
    <Link to={to} className={classes} title={email}>
      {body}
    </Link>
  ) : (
    <span className={classes} title={email}>
      {body}
    </span>
  );
}

/** Rounded-square for a room, a series or a company. */
export function Room({ name, to }: { name: string; to?: string }): ReactElement {
  const body = (
    <>
      <span className="avatar room">
        <Icon name="space" size={12} />
      </span>
      <span className="truncate">{name}</span>
    </>
  );
  return to ? (
    <Link to={to} className="who">
      {body}
    </Link>
  ) : (
    <span className="who">{body}</span>
  );
}

export type GradeValue = 'HEARD' | 'ASSUMED' | 'WRITTEN' | 'CONFIRMED';
const GRADE_WORDS: Record<GradeValue, string> = {
  HEARD: 'heard',
  ASSUMED: 'assumed',
  WRITTEN: 'written',
  CONFIRMED: 'confirmed',
};
/** Evidence grade — dotted → dashed → solid → filled. At rest, never on hover. */
export function Grade({ value }: { value: GradeValue }): ReactElement {
  return <span className={`grade grade-${GRADE_WORDS[value]}`}>{GRADE_WORDS[value]}</span>;
}

export function Pill({
  tone,
  children,
}: {
  tone?: 'green' | 'amber' | 'red';
  children: ReactNode;
}): ReactElement {
  return <span className={`pill ${tone ? `pill-${tone}` : ''}`}>{children}</span>;
}

export function Chip({
  on = false,
  count,
  children,
  onClick,
}: {
  on?: boolean;
  count?: number | string;
  children: ReactNode;
  onClick?: () => void;
}): ReactElement {
  return (
    <button type="button" className={`chip ${on ? 'on' : ''}`} aria-pressed={on} onClick={onClick}>
      {children}
      {count !== undefined ? <span className="count">{count}</span> : null}
    </button>
  );
}

export function Mono({ children }: { children: ReactNode }): ReactElement {
  return <span className="mono">{children}</span>;
}
